<template>
	<div v-show="selectedStudy" class="ScheduledRespondents">
		<div class="d-sr-only-focusable float-right text-decoration-underline" tabindex="0" @keyup.enter="focusToTop" @click="focusToTop" ref="firstFocusable" role="link">Go to top of page</div>
		<h2 class="left-align">Track Respondents</h2>
		<div class="track-sch-respondents-filter-container">
			<v-row>
				<v-col :cols="4">
					<v-row no-gutters>
						<v-col>
							<p class="p3 left-align">Filter by username</p>
						</v-col>
					</v-row>
					<v-row no-gutters>
						<v-col>
							<v-text-field
								outlined
                placeholder="Username"
                single-line
                hide-details
                v-model="filterUserNameValue"
								:aria-label="'Filter by username: ' + filterUserNameValue"
            ></v-text-field>
						</v-col>
					</v-row>
				</v-col>
				<v-divider vertical></v-divider>
				<v-col>
					<v-row no-gutters>
						<v-col :cols="5">
							<p class="p3 left-align">Start Date</p>
						</v-col>
						<v-col :cols="5">
							<p class="p3 left-align">End Date</p>
						</v-col>
					</v-row>
					<v-row no-gutters>
						<v-col :cols="5">
							<v-menu  v-model="startDateMenu" :close-on-content-click="false" offset-y tile>
  							<template v-slot:activator="{ on, attrs }">
								<v-text-field
								hide-details="auto"
								outlined
								type="date"
								:rules="startDateRules"
								v-model="filterStartDateValue"
								v-bind="attrs"
      					v-on="on"
								@keydown.tab='startDateMenu = false'
								:aria-label="'Start Date: ' + filterStartDateValue"
							>
							</v-text-field>
								</template>
								<v-date-picker v-model="filterStartDateValue" color="#42729B" style="margin: 0px; float: left" @input="startDateMenu = false"></v-date-picker>
							</v-menu>
						</v-col>
						<v-col :cols="5">
							<v-menu  v-model="endDateMenu" :close-on-content-click="false" offset-y tile>
  							<template v-slot:activator="{ on, attrs }">
								<v-text-field
								hide-details="auto"
								outlined
								type="date"
								:rules="endDateRules"
								v-model="filterEndDateValue"
								v-bind="attrs"
								v-on="on"
								@keydown.tab='endDateMenu = false'
								:aria-label="'End Date: ' + filterEndDateValue"
							>
							</v-text-field>
								</template>
								<v-date-picker v-model="filterEndDateValue" color="#42729B" style="margin: 0px; float: left" @input="endDateMenu = false"></v-date-picker>
							</v-menu>
						</v-col>
						<v-col :cols="2">
							<v-btn @click="clearFilters()" class="btn-default" text>Clear</v-btn>
						</v-col>
					</v-row>
				</v-col>
			</v-row>
		</div>
		<br>
		<v-btn class="btn-link text-none" text @click="selectAll()">Select All</v-btn>
    <v-btn class="btn-link text-none" text @click="deselectAll()">Deselect All</v-btn>
		<vue-json-to-csv
      :json-data="selected"
      :csv-title="'selected-respondents'"
    >
      <v-btn class="btn-link text-none" text>Export Selected</v-btn>
    </vue-json-to-csv>
    <vue-json-to-csv
      :json-data="respondents"
      :csv-title="'all-respondents'"
    >
      <v-btn class="btn-link text-none" text>Export All</v-btn>
    </vue-json-to-csv>
		<div v-show="headers.length !== 0" class="track-respondents-table-container">
			<v-data-table
				v-model="selected"
				:headers="headers"
				:items="respondents"
				:loading="isLoading"
				item-key="userName"
				show-select
				class="elevation-1"
				:expanded.sync="expanded"
				show-expand
				:page.sync="page"
				:items-per-page="itemsPerPage"
				hide-default-footer
				@page-count="pageCount = $event"
				loading-text="Loading ... Please wait"
				v-on:item-expanded="handleRowExpand($event)"
			>
				<template v-for="h in headers" v-slot:[`header.${h.value}`]="{ header }">
					<v-tooltip  :disabled="h.hover === ''" :key="h.value" bottom color="#333">
						<template v-slot:activator="{ on }">
							<span v-on="on" class="columnTitleText" tabIndex="0" v-on:keyup.enter="(e) => e.target.click()">{{h.text}}</span>
						</template>
						<span>{{h.hover}}</span>
					</v-tooltip>
				</template>
				<!-- for the check box header -->
				<template v-slot:header.data-table-select="{ value, selected }">
					<v-checkbox
            :value="value"
            :selected="selected"
						aria-label="select all"
            hide-details
            class="mt-0"
						@change="checkboxHeader"
					>
					</v-checkbox>
				</template>
				<!-- for the check boxes -->
				<template v-slot:item.data-table-select="{ item, isSelected }">
					<v-checkbox
						:value="isSelected"
            :isSelected="item"
						aria-label="respondent"
            hide-details
            class="mt-0"
						@change="checkboxData(item)"
						>
					</v-checkbox>
				</template>
				<template v-slot:expanded-item="{ headers, item }">
					<td></td>
					<td :colspan="headers.length - 1" style="padding:10px 0px 10px 0px">
						<v-data-table
							:headers="headersScheduledRecalls"
							:items="getScheduledRecalls[item.userUuid]"
							class="elevation-1"
							hide-default-footer
							:loading="scheduledRecallsIsLoading[item.userUuid]"
							loading-text="Loading... Please wait"
						>
							<template v-for="h in headersScheduledRecalls" v-slot:[`header.${h.value}`]="{ header }">
								<span :key="h.value" class="columnTitleText" tabIndex="0" v-on:keyup.enter="(e) => e.target.click()">{{h.text}}</span>
							</template>
						</v-data-table>
					</td>
				</template>
			</v-data-table>
			<div class="pagination-container">
				<v-row>
					<v-col :cols="2">
						<v-text-field
							:value="itemsPerPage"
							label="Items per page"
							type="number"
							:min="minItemsPerPage"
							:max="maxItemsPerPage"
							@input="onItemsPerPageTextInput"
						></v-text-field>
					</v-col>
					<v-col :cols="10">
						<v-pagination
							v-model="page"
							:length="pageCount"
							:total-visible="7"
						></v-pagination>
					</v-col>
				</v-row>
			</div>
		</div>
	</div>
</template>

<script>
import { researcherApiService } from '@/services/researcherApiService.js';
import VueJsonToCsv from 'vue-json-to-csv';
export default {
	name: 'ScheduledRespondents',
	components: { VueJsonToCsv },
	props: {},
	data: () => ({
		filterUserNameValue: '',
		filterStartDateValue: null,
		filterEndDateValue: null,
		isLoading: false,
		studyUuid: '',
		selected: [],
		respondents: [],
		expanded: [],
		page: 1,
		pageCount: 0,
		itemsPerPage: 15,
		/* A map of userUuid to an array of the remaining scheduled recalls for the user */
		scheduledRecalls: {},
		scheduledRecallsIsLoading: {},
		headersScheduledRecalls: [
			{ text: 'User Name', value: 'userName', class: 'data-table-header' },
			{ text: 'Recall Number', value: 'recallNumber', class: 'data-table-header' },
			// { text: 'Scheduled Date', value: 'scheduledDate', class: 'data-table-header' }
			{ text: 'Scheduled Date', value: 'reportingDate', class: 'data-table-header' }
		],
		startDateRules: [
			v => v !== '' || 'Improper date or missing value(s)'
		],
		endDateRules: [
			v => v !== '' || 'Improper date or missing value(s)'
		],
		startDateMenu: false,
		endDateMenu: false,
		minItemsPerPage: 1,
		maxItemsPerPage: 25
	}),
	mounted () {
		this.updateSelected();
		this.endDateRules.push(v => v >= this.filterStartDateValue || 'Date must be greater than or equal to the start date');
	},
	computed: {
		headers: function () {
			return [
				{ text: 'Username', value: 'userName', hover: '', class: 'data-table-header', filter: this.filterUsername },
				{ text: '', value: 'data-table-expand', hover: '', class: 'data-table-header', sortable: false },
				{ text: 'Next Recall #', value: 'nextRecallNo', hover: '', class: 'data-table-header' },
				{ text: 'Next Attempt #', value: 'nextAttemptNo', hover: '', class: 'data-table-header' },
				{ text: 'Next Reporting Date', value: 'nextDate', hover: '', class: 'data-table-header', filter: this.filterDate },
				{ text: 'Total Quits', value: 'totalQuits', hover: '', class: 'data-table-header' },
				{ text: 'Total Completes', value: 'totalCompletes', hover: '', class: 'data-table-header' },
				{ text: 'Total Remaining', value: 'totalRemaining', hover: '', class: 'data-table-header' }
			]
		},
		selectedStudy: function () {
			// If the selectedStudy changes, the radio buttons and checkbox properties need to be updated.
			if (this.studyUuid !== this.$store.getters.getSelectedStudy.studyUuid) {
				this.updateSelected();
			}
			return this.$store.getters.getSelectedStudy;
		},
		getScheduledRecalls: function () {
			return this.scheduledRecalls;
		},
		getScheduledRecallsIsLoading: function () {
			return this.scheduledRecallsIsLoading;
		}
	},
	methods: {
		checkboxHeader (e) {
			if (e) {
				this.selectAll()
			} else {
				this.deselectAll()
			}
		},
		checkboxData (obj) {
			if (!this.selected.includes(obj)) {
				this.selected.push(obj)
			} else {
				for (var i = this.selected.length; i--;) {
					if (this.selected[i] === obj) this.selected.splice(i, 1);
				}
			}
		},
		updateSelected () {
			this.isLoading = true;
			this.studyUuid = this.$store.getters.getSelectedStudy.studyUuid;
			const me = this;
			researcherApiService.loadTrackRespondents(this.studyUuid, this.filterUserNameValue)
				.then(function (response) {
					if (response && response.data) {
						me.respondents = response.data.trackDataList;
					}
					me.isLoading = false;
				}).catch(function (error) {
					console.log(error);
					// Turnoff loading if there was an error
					me.isLoading = false;
				});
		},
		filterUsername (val) {
			if (!this.filterUserNameValue) {
				return true;
			}

			return val.toLowerCase().includes(this.filterUserNameValue.toLowerCase());
		},
		filterDate (val) {
			if (!this.filterStartDateValue && !this.filterEndDateValue) {
				return true;
			}

			const selectedStartDate = new Date(this.filterStartDateValue + 'T00:00:00Z').getTime();
			const selectedEndDate = new Date(this.filterEndDateValue + 'T00:00:00Z').getTime() + 86399999; // adding 1 less millisecond of 24 hours of milliseconds (86400000) is essentially the 'end' timestamp for that date's range of miliseconds
			const recallDate = new Date(val).getTime();

			if (this.filterStartDateValue && this.filterEndDateValue) {
				return (recallDate <= selectedEndDate ? (recallDate >= selectedStartDate) : false);
			}

			if (this.filterStartDateValue) {
				return recallDate >= selectedStartDate;
			}

			if (this.filterEndDateValue) {
				return recallDate <= selectedEndDate;
			}
		},
		clearFilters () {
			this.filterUserNameValue = '';
			this.filterStartDateValue = null;
			this.filterEndDateValue = null;
		},
		selectAll () {
			this.selected = this.respondents;
		},
		deselectAll () {
			this.selected = [];
		},
		handleRowExpand (event) {
			// Only get the data if the event is expanding and not rolling up
			if (event.value === true) {
				this.scheduledRecallsIsLoading[event.item.userUuid] = true;

				// Load the user's data
				const me = this;
				researcherApiService.getUsersRemaingScheduledRecalls(event.item.userUuid, event.item.nextRecallNo)
					.then(function (response) {
						if (response && response.data) {
							console.log('cht1 reseaechAPI response: ', response)
							// To trigger the reactiveness of map data we need to clear the value and set it to a new value
							const m = Object.create(me.scheduledRecalls);
							m[event.item.userUuid] = response.data;
							me.scheduledRecalls = m;

							const recallsLoading = Object.create(me.scheduledRecallsIsLoading);
							recallsLoading[event.item.userUuid] = false;
							me.scheduledRecallsIsLoading = recallsLoading;
						}
					});
			}
		},
		/**
		 * Handles the manual typing of the page size event
		 * @param {*} event - The raised event that holds the entered page size
		 */
		onItemsPerPageTextInput: function (event) {
			let parsedItemsPerPage = parseInt(event, 10);
			if (!isNaN(parsedItemsPerPage)) {
				// If the entered page size is greater than the total number of recalls, set the page size to the total number of recalls
				if (parsedItemsPerPage > this.maxItemsPerPage) {
					parsedItemsPerPage = this.maxItemsPerPage;
				}

				// If the entered page size is less than the minimum page size, set the page size to the minimum page size
				if (parsedItemsPerPage < this.minItemsPerPage) {
					parsedItemsPerPage = this.minItemsPerPage;
				}

				this.itemsPerPage = parsedItemsPerPage;
			}
		},
		focusToTop () {
			var myHeader = document.getElementById('skipToMain')
			myHeader.focus()
		}
	}
}
</script>

<style>
</style>
